'use client';

import { API_ROUTES } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { MultistepForm } from '../../../../../form-controls/MultistepForm/MultistepForm';
import { createZodResolver } from '../../../../../shadcn/components/form';
import { toast } from '../../../../../Toast/Toast';
import { useStepTracking } from '../../../../hooks/useStepTracking';
import { HiddenTrigger, MyAccountSheet } from '../../../../MyAccountSheet/MyAccountSheet';
import { useVerificationApiMutation } from '../../../hooks/useVerificationApiMutation';
import { invalidateMyAccountVerificationQueries } from '../../../utils/invalidateMyAccountVerificationQueries';
import { clearEmailOtpResendLimiters } from '../../../utils/otpResendLimiterStorageKey';
import { isVerificationRateLimitError } from '../../../utils/verificationErrorUtils';
import { useEditEmail } from '../hooks/useEditEmail';
import type { EditEmailFormData, EditEmailSheetProps, SendVerificationPayload, SendVerificationResponse } from '../types';
import { BackHandler } from './steps/BackHandler';
import { EmailStep } from './steps/EmailStep';
import { SuccessStep } from './steps/SuccessStep';
import { VerifyStep } from './steps/VerifyStep';

const EditEmailSheet = ({ open, onOpenChange, user, startOnVerifyStep = false }: EditEmailSheetProps) => {
  const t = useTranslations('my_account');
  const tOtp = useTranslations('form.otp');
  const sheetTitle = t('pages.edit_email.title');
  const shortHeaderTitle = t('pages.edit_email.short_title');
  const [showBackButton, setShowBackButton] = useState(false);
  const [originalEmailAtOpen, setOriginalEmailAtOpen] = useState(user.email ?? '');
  const skipToStepRef = useRef<((stepId: string) => void) | null>(null);
  const setStatusRef = useRef<((status: string) => void) | null>(null);
  const { handleBackNavigation, registerBackHandler } = useEditEmail();
  const queryClient = useQueryClient();
  const { update: updateSession } = useSession();

  const formSchema = z.object({
    email: z.email({ error: t('pages.edit_email.email_step.validation') }),
    otp: z.string().min(6, t('pages.edit_email.verify_step.validation')),
    verificationId: z.string(),
  });

  const form = useForm<EditEmailFormData>({
    resolver: createZodResolver(formSchema),
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      email: user.email ?? '',
      otp: '',
    },
  });

  const { mutateAsync: sendVerification } = useVerificationApiMutation<SendVerificationResponse, SendVerificationPayload>(API_ROUTES.MY_ACCOUNT.VERIFICATION.SEND);

  useEffect(() => {
    if (open) {
      setOriginalEmailAtOpen(user.email ?? '');
      setStatusRef.current?.('');
      clearEmailOtpResendLimiters();
      form.reset({ email: user.email ?? '', otp: '', verificationId: '' });
      if (startOnVerifyStep && user.email) {
        sendVerification({ method: 'email', value: user.email })
          .then(response => {
            if (response) form.setValue('verificationId', response.verificationId);
          })
          .catch(error => {
            if (isVerificationRateLimitError(error)) {
              toast(tOtp('maxAttemptsReached'), { type: 'error' });
              return;
            }
            toast(t('pages.edit_email.email_step.error'), { type: 'error' });
          });
      }
    }
    // Initialise once when the sheet opens; deps intentionally omit stable hooks and props read at open time.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only on open transition
  }, [open]);

  const handleClose = () => {
    form.reset({ email: user.email ?? '', otp: '', verificationId: '' });
    setStatusRef.current?.('');
    setShowBackButton(false);
    onOpenChange(false);
  };

  const handleComplete = () => {
    invalidateMyAccountVerificationQueries(queryClient);
    void updateSession();
    handleClose();
  };

  const handleMultistepFormReady = useCallback(
    (skipToStep: (stepId: string) => void, setStatus: (status: string) => void) => {
      skipToStepRef.current = skipToStep;
      setStatusRef.current = setStatus;
      setStatus('');
      if (startOnVerifyStep) {
        skipToStep('otp');
      }
    },
    [startOnVerifyStep]
  );

  const steps = [
    {
      id: 'email',
      component: (
        <>
          <BackHandler onBack={registerBackHandler} />
          <EmailStep originalEmail={originalEmailAtOpen} />
        </>
      ),
      schema: formSchema.pick({ email: true }),
      'data-track-container': 'Edit Email',
    },
    {
      id: 'otp',
      component: (
        <>
          <BackHandler onBack={registerBackHandler} />
          <VerifyStep onVerificationFailureDone={handleClose} />
        </>
      ),
      schema: formSchema.pick({ otp: true }),
      'data-track-container': 'Verify Email',
    },
    {
      id: 'success',
      component: (
        <>
          <BackHandler onBack={registerBackHandler} />
          <SuccessStep onContinue={handleComplete} />
        </>
      ),
      schema: z.any(),
      'data-track-container': 'Email Verified',
    },
  ];

  const { currentTrackContainer, onStepChange } = useStepTracking(steps);

  const handleBackClick = () => {
    handleBackNavigation(handleClose);
  };

  const handleStepChange = (isFirstStep: boolean, isLastStep: boolean, stepId: string) => {
    onStepChange(isFirstStep, isLastStep, stepId);

    if (startOnVerifyStep) {
      setShowBackButton(false);
      return;
    }
    setShowBackButton(!(isFirstStep || isLastStep));
  };

  return (
    <MyAccountSheet
      trigger={<HiddenTrigger label={sheetTitle} />}
      title={shortHeaderTitle}
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) handleClose();
      }}
      showBackButton={showBackButton}
      onBack={handleBackClick}
      data-track-container={currentTrackContainer}
      data-track-feature="My Account"
    >
      <FormProvider {...form}>
        <MultistepForm
          steps={steps}
          onMultistepFormReady={handleMultistepFormReady}
          showProgressBar={false}
          showNavigation={false}
          onComplete={handleComplete}
          onStepChange={handleStepChange}
          className="h-full px-6 py-8"
        />
      </FormProvider>
    </MyAccountSheet>
  );
};

export { EditEmailSheet };
